import { useEffect, useMemo } from 'react';
import { Box, Dialog, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Button, Input } from '@malash-office/ui-components';
import { useCreateFeedback } from '@/api/feedback';
import { useAppContext } from '@/context/AppContext';
import { useToast } from '@/components/common/toast';
import { apiErrorMessage } from '@/util/apiError';
import { FEEDBACK_CARD_RADIUS, FEEDBACK_INNER_RADIUS, feedbackSurfaces } from '../feedbackTones';
import FeedbackFilesField from './FeedbackFilesField';
import { FeedbackTypePicker, FeedbackUrgencyPicker } from './FeedbackTypePicker';
import { NewFeedbackSeed, useNewFeedbackForm } from './useNewFeedbackForm';

interface NewFeedbackDialogProps {
  seed: NewFeedbackSeed;
  onClose: () => void;
}

/**
 * "פנייה חדשה" — the mockup's modal, field for field: type, urgency, subject, description, the link
 * (filled from the page the user was on), files and the screenshot, and the strip that says what
 * was collected automatically. Full screen on a phone.
 */
const NewFeedbackDialog = ({ seed, onClose }: NewFeedbackDialogProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const surfaces = feedbackSurfaces(theme);
  const form = useNewFeedbackForm(seed);
  const create = useCreateFeedback();
  const { showToast } = useToast();
  const { user } = useAppContext();
  const screenshotUrl = useMemo(
    () => (seed.screenshot ? URL.createObjectURL(seed.screenshot) : null),
    [seed.screenshot]
  );
  useEffect(() => () => screenshotUrl ? URL.revokeObjectURL(screenshotUrl) : undefined, [screenshotUrl]);

  const isBug = form.type === 'bug';

  const submit = async () => {
    const body = form.toBody();
    if (!body) return;
    try {
      const created = await create.mutateAsync({ body, files: form.files, screenshot: form.screenshot });
      showToast(`הפנייה ${created.code} נשלחה. תקבל עדכון כשיהיה טיפול.`, 'success');
      onClose();
    } catch (error) {
      showToast(apiErrorMessage(error, 'שליחת הפנייה נכשלה'), 'error');
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { md: 640 },
          maxWidth: '100%',
          maxHeight: { md: 'calc(100vh - 40px)' },
          borderRadius: { xs: 0, md: FEEDBACK_CARD_RADIUS },
          p: { xs: '18px 16px', md: '22px 24px' },
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }
      }}
      slotProps={{ backdrop: { sx: { bgcolor: surfaces.overlay } } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box component="h2" sx={{ m: 0, fontSize: 20, fontWeight: 700, flex: 1 }}>
          פנייה חדשה
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="סגירה"
          onClick={onClose}
          sx={{
            width: { xs: 40, md: 30 },
            height: { xs: 40, md: 30 },
            borderRadius: '5px',
            border: 'none',
            bgcolor: surfaces.well,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M6 6l12 12M18 6L6 18" stroke={theme.palette.info.dark} strokeWidth="2.4" strokeLinecap="round" />
          </svg>
        </Box>
      </Box>

      <FeedbackTypePicker value={form.type} onChange={form.setType} />
      <FeedbackUrgencyPicker value={form.urgency} onChange={form.setUrgency} />

      <Input
        label="נושא"
        placeholder={isBug ? 'במשפט אחד — מה לא עובד?' : 'במשפט אחד — מה הנושא?'}
        value={form.title}
        onChange={event => form.setTitle(event.target.value)}
        inputProps={{ maxLength: 255 }}
      />
      <Input
        label="תיאור"
        placeholder={
          isBug ? 'מה עשית, מה ציפית שיקרה ומה קרה בפועל' : 'פרט כמה שיותר — למה זה חשוב לך ואיך זה יעזור בעבודה'
        }
        value={form.description}
        onChange={event => form.setDescription(event.target.value)}
        multiline
        rows={4}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Input
          label="קישור לעמוד או לבעיה"
          value={form.link}
          onChange={event => form.setLink(event.target.value)}
          inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
        />
        <Box component="span" sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500, px: 0.75 }}>
          מולא אוטומטית לפי העמוד הנוכחי. אפשר להחליף בקישור אחר.
        </Box>
      </Box>

      <FeedbackFilesField form={form} screenshotUrl={screenshotUrl} />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px 16px',
          px: 1.75,
          py: 1.25,
          borderRadius: FEEDBACK_INNER_RADIUS,
          bgcolor: surfaces.well,
          fontSize: 11.5,
          color: 'text.secondary',
          fontWeight: 600
        }}
      >
        <span>
          נשלח בשם:{' '}
          <Box component="b" sx={{ color: 'text.primary' }}>
            {user ? `${user.firstName} ${user.lastName}` : ''}
          </Box>
        </span>
        <span>
          מודול:{' '}
          <Box component="b" sx={{ color: 'text.primary' }}>
            {seed.module}
          </Box>
        </span>
        <span>נאסף אוטומטית</span>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {form.missing && (
          <Box component="span" sx={{ flex: 1, fontSize: 12, fontWeight: 700, color: 'error.main' }}>
            יש למלא נושא ותיאור
          </Box>
        )}
        <Box sx={{ width: 100 }}>
          <Button variant="outlined" onClick={onClose} disabled={create.isPending}>
            ביטול
          </Button>
        </Box>
        <Box sx={{ width: 130 }}>
          <Button onClick={submit} loading={create.isPending}>
            שליחת פנייה
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default NewFeedbackDialog;
